import { BookOpen, Video, Users, Headphones, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/hooks/use-language";

const About = () => {
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });
  const { t, localePath } = useLanguage();

  const offerings = [
    {
      icon: <BookOpen className="h-6 w-6" />,
      title: t("Krönikor", "Columns"),
      description: t("Expertkunskap om redovisningsregler och branschuppdateringar från våra specialister.", "Expert knowledge about accounting rules and industry updates from our specialists.")
    },
    {
      icon: <Video className="h-6 w-6" />,
      title: t("Instruktionsfilmer", "Instructional videos"),
      description: t("Steg-för-steg-guider för att komma igång och fördjupa er kunskap i systemen.", "Step-by-step guides to get started and deepen your knowledge in the systems.")
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Outsourcing",
      description: t("Vi tar hand om repetitiva redovisningsuppgifter så ni kan fokusera på kärnverksamheten.", "We take care of repetitive accounting tasks so you can focus on your core business.")
    },
    {
      icon: <Headphones className="h-6 w-6" />,
      title: t("Expertsupport", "Expert support"),
      description: t("Tillgång till redovisningsexperter som verkligen förstår era utmaningar.", "Access to accounting experts who truly understand your challenges.")
    }
  ];

  return (
    <section id="about" className="py-32 lg:py-44 bg-white relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-navy/5 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center max-w-6xl mx-auto">
          <div ref={contentRef} className={`transition-all duration-700 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="inline-block text-gold text-sm font-bold uppercase tracking-widest mb-4">
              {t("Om Tiego", "About Tiego")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-normal text-foreground mb-8">
              {t("Specialister inom", "Specialists in")}{" "}
              <span className="italic text-navy">{t("finansiell systemstöd", "financial system support")}</span>
            </h2>
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t(
                  "Tiego är ett specialistbolag med fokus på koncernredovisning och leasinghantering. Våra system är byggda av redovisningsexperter som förstår komplexiteten i era dagliga utmaningar.",
                  "Tiego is a specialist company focused on group accounting and lease accounting. Our systems are built by accounting experts who understand the complexity of your day-to-day challenges."
                )}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t(
                  "Med Mattias Elfgren som VD och redovisningsspecialist i spetsen, kombinerar vi djup teknisk kompetens med gedigen erfarenhet av koncernredovisning enligt K3 och IFRS.",
                  "With Mattias Elfgren as CEO and accounting specialist at the helm, we combine deep technical competence with solid experience in group accounting under K3 and IFRS."
                )}
              </p>
            </div>
            <div className="mt-10">
              <Link to={localePath("/medarbetare")}>
                <Button variant="premium" size="lg">
                  {t("Lär känna oss", "Get to know us")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {offerings.map((offering, index) => (
              <div key={offering.title} className={`bg-background p-6 rounded-2xl shadow-card hover:shadow-card-hover hover:-translate-y-2 transition-all duration-500 group border border-border/50 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/20 to-gold/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-gold">{offering.icon}</div>
                </div>
                <h3 className="font-semibold text-foreground mb-2">{offering.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
